import React, { useContext, useEffect, useRef, useState } from 'react'
import debounce from 'lodash/debounce'
import uniqBy from 'lodash/uniqBy'
import { get } from '~/utils/api'
import { WebsocketContext } from './WebsocketConn'
import { Channel } from 'phoenix'
import { number } from 'zod'

type Client = {
  id: number
  name: string
  avatar: string
}

type User = {
  id: number
  name: string
  avatar: string
}

export type Order = 'createdAsc' | 'createdDesc'

export type CallOnList = {
  id: number
  client: Client
  createdAt: string
  outbound: boolean
  missed: boolean
  ended: boolean
  answered: boolean
  answeredAt: Date | null
  voicebot: boolean
  user: User | null
  tmCampaignId: number | null
  described: boolean
  ver?: number
  duration: number
  ivr: string
  recorded: boolean
  number: string
}

export type Filter = {
  mine: boolean
  // toDescribe: boolean
  answered: boolean
  unanswered: boolean
  inbound: boolean
  outbound: boolean
  dateFrom: string | null
  dateTo: string | null
  order: Order
}

type State = {
  calls: CallOnList[]
  loadHook: () => void
  loadMore: () => void
  setSearch: (search: string) => void
  loading: boolean
  search: string
  filter: Filter
  setFilter: React.Dispatch<React.SetStateAction<Filter>>
  channel?: Channel
  order: Order
  setOrder: (order: Order) => void
}

const defaultFilter: Filter = {
  mine: false,
  // toDescribe: false,
  answered: true,
  unanswered: true,
  inbound: true,
  outbound: true,
  dateFrom: null,
  dateTo: null,
  order: 'createdDesc',
}

export const CallListManagerContext = React.createContext<State>({
  calls: [],
  loadHook: () => {},
  loadMore: () => {},
  setSearch: () => {},
  setFilter: () => {},
  setOrder: () => {},
  loading: false,
  search: '',
  filter: defaultFilter,
  order: 'createdDesc',
})

type Props = { children: React.ReactNode }

const CallListManager: React.FC<Props> = ({ children }) => {
  const wsContext = useContext(WebsocketContext)
  const [loading, setLoading] = useState(false)
  const [search, setSearch] = useState('')
  const [order, setOrder] = useState<Order>('createdDesc')
  const [initialized, setInitialized] = useState(false)
  const [items, setItems] = useState<CallOnList[]>([])
  const [filter, setFilter] = useState(defaultFilter)
  const [channel, setChannel] = useState<Channel | undefined>()
  const searchRef = useRef(search)
  const filterRef = useRef(filter)

  searchRef.current = search
  filterRef.current = filter

  const loadingRef = useRef(loading)
  loadingRef.current = loading

  const download = (params = {}, more = false) => {
    return new Promise<void>(r => {
      setLoading(true)
      loadingRef.current = true

      if (!more) {
        setItems([])
      }

      get<CallOnList[]>(`calls/list`, {
        offset: more ? items.length : 0,
        search,
        ...filter,
        ...params,
      }).then(newItems => {
        if (more) {
          setItems(uniqBy([...items, ...newItems], e => e.id))
        } else {
          setItems(newItems)
        }

        setLoading(false)
        r()
      })
    })
  }

  useEffect(() => {
    const channel = wsContext.socket.channel(
      'phone_call_list:' + window.c.campaignId
    )

    setChannel(channel)

    channel.on('created', (data: CallOnList) => {
      if (searchRef.current) {
        return
      }

      if (
        (!filterRef.current.inbound && !data.outbound) ||
        (!filterRef.current.outbound && data.outbound)
      ) {
        return
      }

      setItems(items => [data, ...items])
    })

    channel.on('updated', (data: CallOnList) => {
      if (searchRef.current) {
        return
      }

      if (
        (!filterRef.current.unanswered && data.missed) ||
        (!filterRef.current.answered && !data.missed) ||
        (filterRef.current.mine &&
          (!data.user || data.user.id != window.c.userId))
      ) {
        setItems(items => items.filter(item => item.id != data.id))

        return
      }

      if (
        (!filterRef.current.inbound && !data.outbound) ||
        (!filterRef.current.outbound && data.outbound)
      ) {
        return
      }

      setItems(items =>
        items.find(item => item.id == data.id)
          ? items.map(item =>
              item.id == data.id ? { ...data, ver: (item.ver || 0) + 1 } : item
            )
          : [data, ...items]
      )
    })

    channel.join()
  }, [])

  const loadHook = () => {
    useEffect(() => {
      if (initialized) {
        return
      }

      download().then(() => {
        setInitialized(true)
      })
    }, [])
  }

  const [searchDebounce] = useState(() =>
    debounce(search => {
      download({ search })
    }, 500)
  )

  useEffect(() => {
    if (!initialized) {
      return
    }

    searchDebounce(search)
  }, [search])

  const loadMore = () => {
    if (loadingRef.current) {
      return
    }

    download({}, true)
  }

  useEffect(() => {
    if (!initialized) {
      return
    }

    download()
  }, Object.values(filter))

  return (
    <CallListManagerContext.Provider
      value={{
        calls: items,
        loadHook,
        loading,
        search,
        loadMore,
        setSearch,
        filter,
        setFilter,
        channel,
        order,
        setOrder,
      }}
    >
      {children}
    </CallListManagerContext.Provider>
  )
}

export default CallListManager
